<template>
  <v-dialog
    v-if="currentSubStep > 1"
    v-model="visible"
    persistent
    max-width="600px"
    scrollable
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        v-if="!isFloating"
        color="yellow"
        style="height: 45px; width: 45px;"
        elevation="2"
        fab
        small
        v-bind="attrs"
        v-on="on"
        class="ml-2 mr-2"
      >
        <v-icon color="green darken-2">
          mdi-pencil
        </v-icon>
      </v-btn>
      <v-btn
        v-else
        style="z-index: 9; left: 100px; height: 40px; width: 40px;"
        elevation="2"
        color="yellow"
        fab
        fixed
        bottom
        left
        v-bind="attrs"
        v-on="on"
      >
        <v-icon color="green darken-2">
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h5">Case Notes</span>
        <print-notes v-if="user && notes.length > 0" :user="user" small :showPencilIcon="false"></print-notes>
      </v-card-title>

      <v-card-text class="text-area-container">
        <v-textarea
          filled
          label="Type your notes here."
          required
          v-model="currentNote"
          :maxlength="maxNoteLength"
          counter
          rows="4"
          no-resize
          @keydown.ctrl.enter="saveNotes"
        ></v-textarea>
      </v-card-text>

      <v-card-text class="list-container">
        <div v-if="notes.length > 0" class="notes-list">
          <div class="notes-list-header"><strong>My Saved Notes</strong></div>
          <div
            v-for="(note, i) in notes"
            :key="i"
            class="note-card"
            :class="{ 'note-card-selected': selectedNote === i }"
            @click="selectedNoteList(i)"
          >
            <div class="note-card-header">
              <span class="note-case-badge">Case {{ note.stepId }}</span>
              <span class="note-category-text">{{ note.category }}</span>
            </div>
            <div class="note-card-body">{{ truncateNote(note.body) }}</div>
            <div class="note-actions">
              <v-btn icon small class="note-action-btn" @click.stop="selectedNoteList(i)">
                <v-icon small color="green">mdi-pencil</v-icon>
              </v-btn>
              <confirm
                buttonIcon="mdi-delete"
                buttonColor="red"
                buttonSize="small"
                description="Delete this note?"
                :buttonConfirmCallback="
                  () => {
                    deletedNoteList(i);
                  }
                "
              ></confirm>
            </div>
          </div>
        </div>
        <div v-else class="no-notes">
          <v-icon large color="grey lighten-1">mdi-note-text-outline</v-icon>
          <p>No notes yet. Start typing above to create one!</p>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-snackbar v-model="alert.show" :timeout="2000" color="primary" text>
          {{ alert.text }}
          <template v-slot:action="{ attrs }">
            <v-btn color="blue" text v-bind="attrs" @click="alert.show = false">
              Close
            </v-btn>
          </template>
        </v-snackbar>
        <v-btn color="grey" outlined small class="font-weight-bold" @click="closeNotes">
          <v-icon small class="mr-1">mdi-close</v-icon>
          Close
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          v-show="noteForUpdate"
          color="green"
          outlined
          small
          class="font-weight-bold"
          @click="clearCurrentNote()"
        >
          New Note
        </v-btn>
        <v-btn color="green" small class="white--text font-weight-bold" @click="saveNotes">
          {{ noteForUpdate ? 'Save Edit' : 'Save' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";
import Confirm from "/src/components/modals/Confirm.vue";
import PrintNotes from "/src/components/pdf/PrintNotes.vue";
export default {
  name: "Notes",
  components: {
    Confirm,
    PrintNotes,
  },
  props: {
    user: {
      type: Object,
      required: true,
      default: {},
    },
    currentStep: {
      type: Number,
      required: true,
      default: 1,
    },
    currentSubStep: {
      type: Number,
      required: true,
      default: 1,
    },
    currentTitle: {
      type: String,
      required: false,
      default: "",
    },
    stepName: {
      type: String,
      required: false,
      default: "",
    },
    sectionName: {
      type: String,
      required: false,
      default: "",
    },
    avatarName: {
      type: String,
      required: false,
      default: "",
    },
    isFloating: {
      type: Boolean,
      defautl: false,
      reuired: false,
    },
  },
  data() {
    return {
      visible: false,
      selectedNote: null,
      currentNote: "",
      noteForUpdate: false,
      idNoteForUpdate: 0,
      notes: [],
      maxNoteLength: 500,
      notePreviewLength: 100,
      alert: {
        show: false,
        text: "Note deleted",
      },
    };
  },
  computed: {
    remainingChars() {
      return this.maxNoteLength - (this.currentNote ? this.currentNote.length : 0);
    },
  },
  watch: {
    visible(newValue) {
      if (!newValue) {
        this.clearCurrentNote();
      }
    },
  },
  beforeMount() {
    this.fetchNotes();
  },
  methods: {
    async fetchNotes() {
      let uuid = this.user.uuid;
      let notesApi;
      try {
        let response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/notes`,
          {
            params: { uuid, active: 1 },
          }
        );
        notesApi = response.data.data;
      } catch (err) {
        console.log(err);
        return;
      }
      let notes = [];
      notesApi.forEach(function(element) {
        notes.push({
          id: element.id,
          stepId: element.id_step,
          category: element.section_name || element.title || 'Note',
          body: element.note,
        });
      });
      this.notes = notes;
    },
    closeNotes() {
      this.visible = false;
      this.currentNote = "";
    },
    getPayload() {
      return {
        uuid: this.user.uuid,
        id_step: this.currentStep,
        id_substep: this.currentSubStep,
        step_name: this.stepName || `Case ${this.currentStep}`,
        section_name: this.sectionName || `Section ${this.currentSubStep}`,
        avatar_name: this.avatarName || '',
        title: this.currentTitle
          ? this.currentTitle
          : `Note ${this.currentSubStep}`,
        fName: this.user.fName,
        lName: this.user.lName,
        email: this.user.email,
        note: this.currentNote,
        active: 1,
        deleted: 0,
      };
    },
    saveNotes() {
      // Don't save empty notes
      if (!this.currentNote || this.currentNote.trim() === '') {
        this.showAlert("Please enter a note before saving.");
        return;
      }

      if (this.noteForUpdate) {
        let idNote = this.notes[this.idNoteForUpdate].id;
        this.notes[this.idNoteForUpdate].body = this.currentNote;
        const payload = this.getPayload();
        axios
          .put(`${process.env.VUE_APP_API_ENDPOINT}/notes/` + idNote, payload)
          .then((response) => {
            this.showAlert("Note updated!");
            this.clearCurrentNote();
          })
          .catch((error) => {
            console.log("Error updating note:", error);
            this.showAlert("Failed to update note.");
          });
      } else {
        const payload = this.getPayload();
        axios
          .post(`${process.env.VUE_APP_API_ENDPOINT}/note`, payload)
          .then((response) => {
            this.notes.push({
              id: response.data.data.id,
              stepId: this.currentStep,
              category: this.sectionName || `Note ${this.currentSubStep}`,
              body: this.currentNote,
            });
            this.showAlert("Note saved!");
            this.currentNote = "";
          })
          .catch((error) => {
            console.log("Error saving note:", error);
            this.showAlert("Failed to save note.");
          });
      }
    },
    clearCurrentNote() {
      this.currentNote = "";
      this.noteForUpdate = false;
      this.selectedNote = null;
    },
    selectedNoteList(i) {
      this.noteForUpdate = true;
      this.idNoteForUpdate = i;
      this.currentNote = this.notes[i].body;
    },
    deletedNoteList(i) {
      this.clearCurrentNote();
      let id = this.notes[i].id;
      this.notes.splice(i, 1);
      var data = "";

      var config = {
        method: "put",
        url: `${process.env.VUE_APP_API_ENDPOINT}/softDelete/${id}`,
        headers: {},
        data: data,
      };

      const self = this;
      axios(config)
        .then(function(response) {
          self.showAlert("Note deleted!");
        })
        .catch(function(error) {
          self.showAlert("Failed to delete");
          console.log(error);
        });
    },
    showAlert(text) {
      this.alert.text = text;
      this.alert.show = true;
    },
    truncateNote(text) {
      if (!text) return '';
      if (text.length <= this.notePreviewLength) return text;
      return text.substring(0, this.notePreviewLength) + '...';
    },
  },
};
</script>

<style scoped>
.text-area-container {
  padding-bottom: 0;
}

.text-area-container .v-textarea {
  margin-bottom: 0;
}

.text-area-container >>> .v-textarea textarea {
  line-height: 1.3 !important;
  padding: 12px 4px 12px 0  !important;
}

.text-area-container >>> .v-input__slot {
  background: #fffde7 !important;
  border: 2px solid #fff59d !important;
  border-radius: 8px !important;
  box-shadow: 2px 2px 5px rgba(0,0,0,0.1) !important;
}

.text-area-container >>> .v-text-field--filled > .v-input__control > .v-input__slot {
  background: #fffde7 !important;
}

.text-area-container >>> .v-input--is-focused .v-input__slot {
  border-color: #fbc02d !important;
}

.text-area-container >>> .v-text-field > .v-input__control > .v-input__slot:before,
.text-area-container >>> .v-text-field > .v-input__control > .v-input__slot:after {
  border: none !important;
}

.list-container {
  height: 350px;
  overflow-y: auto;
  padding: 10px 8px 0;
  background: #fffcf1;
  border-top: 1px solid #ffedbf
}


.notes-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.notes-list-header {
  font-size: 0.85rem;
  font-weight: 600;
  color: #4d4d4d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 10px;
  margin-bottom: 0px;
}

.note-card {
  background: #fffde7;
  border-radius: 8px;
  padding: 12px;
  padding-right: 36px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid #fff59d;
  position: relative;
  box-shadow: 2px 2px 5px rgba(0,0,0,0.1);
}

.note-card:hover {
  background: #fff9c4;
  border-color: #ffee58;
}

.note-card-selected {
  background: #fff9c4;
  border-color: #fbc02d;
}

.note-card-header {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 6px;
}

.note-case-badge {
  background: #4caf50;
  color: white;
  font-size: 0.65rem;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 10px;
  flex-shrink: 0;
  white-space: nowrap;
  margin-top: 2px;
}

.note-category-text {
  padding-top: 2px;
  font-size: 0.85rem;
  color: #555;
  font-weight: 700;
}

.note-card-body {
  font-size: 0.8rem;
  color: #555;
  line-height: 1.4;
}

.note-actions {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
}

.note-action-btn {
  width: 28px !important;
  height: 28px !important;
}

.no-notes {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #999;
}

.no-notes p {
  margin-top: 12px;
  font-size: 0.9rem;
}
</style>
